import {SearchResultDTO} from '../model/SearchResultDTO'
import {useState} from 'react'
import {SearchItem} from '../model/SearchItem'
import {useSearchParams} from 'react-router-dom'
import {pageSizeURLSearchParamsName} from '../SearchParams'
import {toPageSize} from '../../util/component/search'
import {GridCallbackDetails, GridRowId, GridRowSelectionModel} from '@mui/x-data-grid'

export const useSearchResultControl = ({
                                           searchResult,
                                           page
                                       }: {
    searchResult: SearchResultDTO | undefined,
    page: number
}) => {

    const [legacyView, setLegacyView] = useState<boolean>(false)
    const [addItemsToListModalVisible, setAddItemsToListModalVisible] = useState<boolean>(false)
    const [searchParams] = useSearchParams()
    const pageSize = toPageSize(searchParams.get(pageSizeURLSearchParamsName))

    const startIndex = page * pageSize - pageSize + 1
    const endIndex = startIndex + (searchResult?.elements?.length ?? 0) - 1

    const noFoundItems = !searchResult || searchResult?.elements?.length === 0

    const [selectedItems, setSelectedItems] = useState<Map<GridRowId, SearchItem>>(new Map())

    const handleRowSelectionChange = (
        newSelectionModel: GridRowSelectionModel,
        _details: GridCallbackDetails
    ) => {
        setSelectedItems(prev => {
            //keep previous selection in memory
            const newMap = new Map(prev)

            //if a row has been selected
            newSelectionModel.forEach((id) => {
                const item = searchResult?.elements.find((el) => el.id === id)
                if (item) {
                    newMap.set(id, item)
                }
            })

            //if a row has been unselected
            const pageItemIds = searchResult?.elements.map((el) => el.id) ?? []
            pageItemIds.forEach((id) => {
                if (!newSelectionModel.includes(id)) {
                    newMap.delete(id)
                }
            })

            return newMap
        })
    }

    const isSelected = (item: SearchItem) => {
        return selectedItems.has(item.id)
    }

    const addToMultiSelect = (itemsToAdd: SearchItem[]) => {
        setSelectedItems(prev => {
            const newMap = new Map(prev)
            itemsToAdd.forEach(item => newMap.set(item.id, item))
            return newMap
        })
    }

    const addPageResultToSelection = () => {
        addToMultiSelect(searchResult?.elements || [])
    }

    const removeFromMultiSelect = (itemToRemove: SearchItem) => {
        setSelectedItems(prev => {
            const newMap = new Map(prev)
            newMap.delete(itemToRemove.id)
            return newMap
        })
    }

    const resetMultiSelect = () => {
        setSelectedItems(new Map())
    }

    const clearPageSelection = () => {
        const currentPageIds = searchResult?.elements.map((result) => String(result.id)) ?? []
        setSelectedItems(prev => {
            const newMap = new Map(prev)
            currentPageIds.forEach(id => newMap.delete(id))
            return newMap
        })
    }

    const areAllItemsInPageSelected = () => {
        if (!searchResult?.elements.length) return false

        return searchResult.elements.every((returnedItem) =>
            selectedItems.has(returnedItem.id)
        )
    }

    const isAnItemInPageSelected = () => {
        return !!searchResult?.elements.some((returnedItem) =>
            selectedItems.has(returnedItem.id)
        )
    }

    const handleAddMultipleItemsToList = () => {
        setAddItemsToListModalVisible(true)
    }

    const handleAddSingleItemToList = (item: SearchItem) => {
        setAddItemsToListModalVisible(true)
        setSelectedItems(prev => {
            const newMap = new Map(prev)
            newMap.set(item.id, item)
            return newMap
        })
    }

    return {
        noFoundItems,
        addPageResultToSelection,
        clearPageSelection,
        handleAddMultipleItemsToList,
        setLegacyView,
        legacyView,
        isAnItemInPageSelected,
        areAllItemsInPageSelected,
        startIndex,
        endIndex,
        addToMultiSelect,
        removeFromMultiSelect,
        resetMultiSelect,
        handleAddSingleItemToList,
        isSelected,
        pageSize,
        addItemsToListModalVisible,
        setAddItemsToListModalVisible,
        selectedItems: Array.from(selectedItems.values()),
        selectedItemIds: new Set(selectedItems.keys()),
        handleRowSelectionChange
    }
}
export type SearchResultControl = ReturnType<typeof useSearchResultControl>